<template>
  <div
    class="admin-start-hub"
    :data-brand-theme="themeKey || undefined"
  >
    <div class="admin-start-hub__panel">
      <StartHubOrient
        :orient="payload.orient"
        :greeting-template="labels.greeting"
      />

      <section
        class="admin-start-hub__section"
        aria-labelledby="admin-start-hub-attention-heading"
      >
        <h2 id="admin-start-hub-attention-heading" class="admin-start-hub__section-title">
          {{ labels.attentionHeading }}
        </h2>

        <div v-if="attentionCards.length" class="admin-start-hub__attention-grid">
          <StartHubTicketsCard
            v-if="ticketsCard"
            :key="attentionCardKey(ticketsCard)"
            class="admin-start-hub__span-half"
            :card="ticketsCard"
            :cta-label="labels.openTicketsCta"
          />
          <StartHubNotificationsCard
            v-if="notificationsCard"
            :key="attentionCardKey(notificationsCard)"
            class="admin-start-hub__span-half"
            :card="notificationsCard"
            :unread-suffix="labels.unreadSuffix"
          />
          <template v-for="card in otherAttention" :key="attentionCardKey(card)">
            <div class="admin-start-hub__card admin-start-hub__tickets admin-start-hub__span-third">
              <div>
                <div class="admin-start-hub__card-label">
                  <span class="admin-start-hub__dot" aria-hidden="true"></span>
                  <span>{{ card.label }}</span>
                </div>
                <p class="admin-start-hub__count">{{ Number(card.count || 0).toLocaleString('it-IT') }}</p>
              </div>
              <a class="admin-start-hub__cta" :href="card.href">{{ labels.open }}</a>
            </div>
          </template>
        </div>
        <p v-else class="admin-start-hub__empty">{{ labels.attentionEmpty }}</p>
      </section>

      <section
        v-if="hasAccessi"
        class="admin-start-hub__accessi"
        aria-labelledby="admin-start-hub-accessi-heading"
      >
        <h2 id="admin-start-hub-accessi-heading" class="admin-start-hub__section-title">
          {{ labels.launcher }}
        </h2>

        <StartHubTileRow :title="labels.cms" :tiles="payload.cms_tiles || []" />
        <StartHubTileRow :title="labels.catalog" :tiles="payload.catalog_tiles || []" />
        <StartHubTileRow :tiles="payload.tiles || []" />
      </section>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@admin-panel/eventBus';
import StartHubOrient from './components/StartHubOrient.vue';
import StartHubTicketsCard from './components/StartHubTicketsCard.vue';
import StartHubNotificationsCard from './components/StartHubNotificationsCard.vue';
import StartHubTileRow from './components/StartHubTileRow.vue';
import './start-hub-styles.css';

export default {
  name: 'AdminStartHub',
  components: {
    StartHubOrient,
    StartHubTicketsCard,
    StartHubNotificationsCard,
    StartHubTileRow,
  },
  props: {
    initialPayload: { type: Object, required: true },
    labels: { type: Object, required: true },
    attentionRefreshRoute: { type: String, default: '' },
  },
  data() {
    return {
      payload: this.initialPayload,
      echoChannelName: null,
      attentionFetchGeneration: 0,
      onNotificationRead: null,
    };
  },
  computed: {
    themeKey() {
      return this.payload.theme?.key || null;
    },
    attentionCards() {
      return Array.isArray(this.payload.attention) ? this.payload.attention : [];
    },
    ticketsCard() {
      return this.attentionCards.find((card) => card.key === 'open-tickets') || null;
    },
    notificationsCard() {
      return this.attentionCards.find((card) => card.key === 'unread-notifications') || null;
    },
    otherAttention() {
      return this.attentionCards.filter(
        (card) => card.key !== 'open-tickets' && card.key !== 'unread-notifications',
      );
    },
    hasAccessi() {
      return (
        (this.payload.cms_tiles || []).length > 0
        || (this.payload.catalog_tiles || []).length > 0
        || (this.payload.tiles || []).length > 0
      );
    },
  },
  created() {
    this.onNotificationRead = () => {
      this.fetchAttention();
    };
    EventBus.on('notificationRead', this.onNotificationRead);
    this.subscribeEcho();
  },
  beforeUnmount() {
    if (this.onNotificationRead) {
      EventBus.off('notificationRead', this.onNotificationRead);
    }
    this.unsubscribeEcho();
  },
  methods: {
    attentionCardKey(card) {
      const count = Number(card?.count || 0);
      const items = Array.isArray(card?.items) ? card.items.length : 0;

      return `${card?.key || 'card'}-${count}-${items}`;
    },
    applyAttentionPayload(data) {
      if (!data || typeof data !== 'object' || !Array.isArray(data.attention)) {
        return;
      }

      this.payload = {
        ...this.payload,
        attention: data.attention,
      };
    },
    fetchAttention() {
      if (!this.attentionRefreshRoute) {
        return;
      }

      const generation = ++this.attentionFetchGeneration;

      axios.get(this.attentionRefreshRoute).then((res) => {
        if (generation !== this.attentionFetchGeneration) {
          return;
        }
        this.applyAttentionPayload(res.data);
      }).catch(() => {});
    },
    subscribeEcho() {
      const userId = window.Laravel?.userId;
      if (!window.Echo || !userId) {
        return;
      }

      this.echoChannelName = `App.User.${userId}`;
      window.Echo.private(this.echoChannelName)
        .listen('.in-app-notification.created', () => {
          this.fetchAttention();
        });
    },
    unsubscribeEcho() {
      if (!window.Echo || !this.echoChannelName) {
        return;
      }
      window.Echo.private(this.echoChannelName)
        .stopListening('.in-app-notification.created');
    },
  },
};
</script>
